import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function SalesExecutiveWiseSales() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedSalesMan, setSelectedSalesMan] = useState('');
    const [dropdownSalesManOptions, setDropdownSalesManOptions] = useState([]);
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    const [totalQty, setTotalQty] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [totalAddDiscount, setTotalAddDiscount] = useState(0);
    const [totalCashDiscount, setTotalCashDiscount] = useState(0);
    const [totalTaxable, setTotalTaxable] = useState(0);
    const [totalCGST, setTotalCGST] = useState(0);
    const [totalSGST, setTotalSGST] = useState(0);
    const [totalIGST, setTotalIGST] = useState(0);
    const [totalNet, setTotalNet] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchDropdownSalesManOptions();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        let qty = 0, amount = 0, discount = 0, addDiscount = 0, cashDiscount = 0;
        let taxable = 0, cgst = 0, sgst = 0, igst = 0, net = 0;
        dataItem.forEach(item => {
            qty += item.Quantity;
            amount += item.Amount;
            discount += item.DiscountAmount;
            addDiscount += item.AdditionalDiscountAmount;
            cashDiscount += item.CashDiscount;
            taxable += item.TaxableAmount;
            cgst += item.CGSTAmount;
            sgst += item.SGSTAmount;
            igst += item.IGSTAmount;
            net += item.TotalAmount;
        });
        setTotalQty(qty);
        setTotalAmount(amount);
        setTotalDiscount(discount);
        setTotalAddDiscount(addDiscount);
        setTotalCashDiscount(cashDiscount);
        setTotalTaxable(taxable);
        setTotalCGST(cgst);
        setTotalSGST(sgst);
        setTotalIGST(igst);
        setTotalNet(net);
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const fetchDropdownSalesManOptions = () => {
        fetchData('SaleApi', `/SalesMan/${companyId}/${branchId}`, token)
            .then(r => r.response_data).then(data => setDropdownSalesManOptions(data || []))
            .catch(err => console.error('Error fetching SalesMan:', err));
    };

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            const salesManId = selectedSalesMan || 0;
            try {
                const response = await fetchData('ReportApi', `/SalesSummaryExecutive/${fromDate}/${toDate}/${companyId}/${branchId}/${salesManId}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Sales Executive Wise Sales:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    // Group data by SalesManName, then by BillNumber
    const groupedData = {};
    filteredDataItem.forEach(item => {
        const salesMan = item.SalesManName || 'Unknown';
        if (!groupedData[salesMan]) groupedData[salesMan] = {};
        const billNo = item.BillNumber || 'N/A';
        if (!groupedData[salesMan][billNo]) groupedData[salesMan][billNo] = [];
        groupedData[salesMan][billNo].push(item);
    });

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sales Executive Wise Sales');

        const rightAlignColumns = [
            'Quantity', 'Rate', 'Amount', 'Discount %', 'Discount Amount',
            'Add Disc %', 'Add Disc Amount', 'Cash Discount',
            'Taxable Amount', 'CGST', 'SGST', 'IGST', 'Total Amount'
        ];

        worksheet.mergeCells('A1:V1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = `Sales Executive Wise Sales Report From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Date', 'Time', 'Bill No', 'Code', 'Item Name', 'HSN Code', 'GST %',
            'Quantity', 'Rate', 'Amount', 'Discount %', 'Discount Amount',
            'Add Disc %', 'Add Disc Amount', 'Cash Discount',
            'Taxable Amount', 'CGST', 'SGST', 'IGST', 'Total Amount',
            'Mode Of Payment', 'Remarks'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 15 }));

        Object.keys(groupedData).forEach(salesMan => {
            // SalesMan header row
            const smRow = worksheet.addRow([`Sales Executive: ${salesMan}`]);
            smRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF1565C0' } };
            worksheet.mergeCells(smRow.number, 1, smRow.number, headerRowValues.length);

            Object.keys(groupedData[salesMan]).forEach(billNo => {
                const items = groupedData[salesMan][billNo];
                // Bill header row
                const billRow = worksheet.addRow([`  Bill No: ${billNo}`]);
                billRow.getCell(1).font = { bold: true, italic: true };
                worksheet.mergeCells(billRow.number, 1, billRow.number, headerRowValues.length);

                items.forEach(rowData => {
                    const dataRow = [
                        formatDate(rowData.BillDate), rowData.BillTime, rowData.BillNumber,
                        rowData.Code, rowData.ItemName, rowData.HSNCode, rowData.GSTRate,
                        rowData.Quantity, parseFloat(rowData.Rate.toFixed(2)),
                        parseFloat(rowData.Amount.toFixed(2)), rowData.DiscountPercentage,
                        parseFloat(rowData.DiscountAmount.toFixed(2)), rowData.AdditionalDiscountPercentage,
                        parseFloat(rowData.AdditionalDiscountAmount.toFixed(2)),
                        parseFloat(rowData.CashDiscount.toFixed(2)),
                        parseFloat(rowData.TaxableAmount.toFixed(2)),
                        parseFloat(rowData.CGSTAmount.toFixed(2)),
                        parseFloat(rowData.SGSTAmount.toFixed(2)),
                        parseFloat(rowData.IGSTAmount.toFixed(2)),
                        parseFloat(rowData.TotalAmount.toFixed(2)),
                        rowData.ModeOfPayment, rowData.Remarks
                    ];
                    const row = worksheet.addRow(dataRow);
                    row.eachCell((cell, colNumber) => {
                        const headerName = headerRowValues[colNumber - 1];
                        if (rightAlignColumns.includes(headerName)) {
                            cell.alignment = { vertical: 'middle', horizontal: 'right' };
                        } else {
                            cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        }
                        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                    });
                });
            });
        });

        // Total row
        const totalRowValues = [
            '', '', '', '', '', '', 'Total',
            totalQty, '', totalAmount.toFixed(2), '', totalDiscount.toFixed(2),
            '', totalAddDiscount.toFixed(2), totalCashDiscount.toFixed(2),
            totalTaxable.toFixed(2), totalCGST.toFixed(2), totalSGST.toFixed(2),
            totalIGST.toFixed(2), totalNet.toFixed(2), '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'SalesExecutiveWiseSales.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Sales Executive Wise Sales Report</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Sales Man</label>
                            <select className="pg-input" value={selectedSalesMan}
                                onChange={e => setSelectedSalesMan(e.target.value)}>
                                <option value="">-- Select Sales Man --</option>
                                {dropdownSalesManOptions.map(o => <option key={o.Id} value={o.Id}>{o.Name}</option>)}
                            </select>
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon /> Refresh
                        </button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel} disabled={filteredDataItem.length === 0}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Time</th>
                            <th>Bill No</th>
                            <th>Code</th>
                            <th>Item Name</th>
                            <th>HSN Code</th>
                            <th style={{ textAlign: 'right' }}>GST %</th>
                            <th style={{ textAlign: 'right' }}>Quantity</th>
                            <th style={{ textAlign: 'right' }}>Rate</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Discount %</th>
                            <th style={{ textAlign: 'right' }}>Discount Amount</th>
                            <th style={{ textAlign: 'right' }}>Add Disc %</th>
                            <th style={{ textAlign: 'right' }}>Add Disc Amount</th>
                            <th style={{ textAlign: 'right' }}>Cash Discount</th>
                            <th style={{ textAlign: 'right' }}>Taxable Amount</th>
                            <th style={{ textAlign: 'right' }}>CGST</th>
                            <th style={{ textAlign: 'right' }}>SGST</th>
                            <th style={{ textAlign: 'right' }}>IGST</th>
                            <th style={{ textAlign: 'right' }}>Total Amount</th>
                            <th>Mode Of Payment</th>
                            <th>Remarks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? <tr><td colSpan={22} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : Object.keys(groupedData).map(salesMan => (
                                <React.Fragment key={salesMan}>
                                    {/* Sales Man Group Header */}
                                    <tr style={{ background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)' }}>
                                        <td colSpan={22} style={{ color: '#fff', fontWeight: 700, padding: '8px 12px', fontSize: '13px' }}>
                                            Sales Executive: {salesMan}
                                        </td>
                                    </tr>
                                    {Object.keys(groupedData[salesMan]).map(billNo => {
                                        const items = groupedData[salesMan][billNo];
                                        return (
                                            <React.Fragment key={billNo}>
                                                {/* Bill Group Header */}
                                                <tr style={{ background: '#e3f2fd' }}>
                                                    <td colSpan={22} style={{ fontWeight: 600, padding: '6px 12px', fontSize: '12px', color: '#1565C0' }}>
                                                        Bill No: {billNo}
                                                    </td>
                                                </tr>
                                                {items.map((data, index) => (
                                                    <tr key={`${billNo}-${index}`}>
                                                        <td>{formatDate(data.BillDate)}</td>
                                                        <td>{data.BillTime}</td>
                                                        <td>{data.BillNumber}</td>
                                                        <td>{data.Code}</td>
                                                        <td>{data.ItemName}</td>
                                                        <td>{data.HSNCode}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.GSTRate}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.Quantity}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.Rate ? data.Rate.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.Amount ? data.Amount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.DiscountPercentage}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.DiscountAmount ? data.DiscountAmount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.AdditionalDiscountPercentage}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.AdditionalDiscountAmount ? data.AdditionalDiscountAmount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.CashDiscount ? data.CashDiscount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.TaxableAmount ? data.TaxableAmount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.CGSTAmount ? data.CGSTAmount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.SGSTAmount ? data.SGSTAmount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.IGSTAmount ? data.IGSTAmount.toFixed(2) : '0.00'}</td>
                                                        <td style={{ textAlign: 'right' }}>{data.TotalAmount ? data.TotalAmount.toFixed(2) : '0.00'}</td>
                                                        <td>{data.ModeOfPayment}</td>
                                                        <td>{data.Remarks}</td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            ))
                        }
                        {filteredDataItem.length > 0 && (
                            <tr style={{ background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)', color: '#e8f0f8', fontWeight: 700 }}>
                                <td colSpan={7} style={{ textAlign: 'right', padding: '6px 10px' }}>Total:</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalQty}</td>
                                <td style={{ padding: '6px 10px' }}></td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalAmount.toFixed(2)}</td>
                                <td style={{ padding: '6px 10px' }}></td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalDiscount.toFixed(2)}</td>
                                <td style={{ padding: '6px 10px' }}></td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalAddDiscount.toFixed(2)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalCashDiscount.toFixed(2)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalTaxable.toFixed(2)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalCGST.toFixed(2)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalSGST.toFixed(2)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalIGST.toFixed(2)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalNet.toFixed(2)}</td>
                                <td colSpan={2} style={{ padding: '6px 10px' }}></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default SalesExecutiveWiseSales;
